import { z } from 'zod';
import { AcrMode, AcrPeriod, AreaKey, Currency, CustomerType, DesignationStatus, Market, Segment, SpecStatus, } from './domain.js';
import { CentsSchema, FxMicroSchema } from './money.js';
import { PromoCatalogue, PromoScenarioInput } from './promotions.js';
/**
 * A scenario — everything a consultant enters about one deal.
 *
 * In the legacy tool this lived across seven `localStorage` keys on the
 * consultant's laptop, unencrypted and surviving logout. It holds customer names,
 * deal values and a read of the customer's estate, so it now lives server-side
 * against an authenticated user. See security.md §1.
 *
 * Every object is `.strict()`: unknown keys are rejected, which blocks
 * mass-assignment. Money fields are integer cents.
 */
/** One row of the workload breakdown. */
export const WorkloadRow = z
    .object({
    id: z.string().min(1).max(64),
    name: z.string().min(1).max(120),
    /** A workload category key. Unknown keys resolve to the fallback category. */
    category: z.string().min(1).max(64),
    /** In `currency`, for the period given by `period`. */
    amount: CentsSchema,
})
    .strict();
/** The single-figure alternative to the workload breakdown. */
export const ManualAcr = z
    .object({
    /** Net new consumption — what the bands measure. */
    incremental: CentsSchema,
    /** Existing environment, excluded from incremental. */
    baseline: CentsSchema,
    ofWhichDatabases: CentsSchema,
    ofWhichAgentic: CentsSchema,
    /**
     * Defender for Cloud and Sentinel combined. Single-figure mode cannot
     * separate them, so both accelerators size on the same number — only the
     * workload breakdown distinguishes them.
     */
    ofWhichDefenderSentinel: CentsSchema,
})
    .strict();
/**
 * Pre-qualification. Numeric fields are counts except `entraP1UsagePct`, which is
 * a percentage. Booleans are conditions the tool cannot verify — each defaults to
 * false and therefore blocks, because an unverifiable condition must never
 * inflate a claim.
 */
export const Prequalification = z
    .object({
    customerType: CustomerType,
    m365Seats: z.number().int().min(0).max(10_000_000),
    intuneSeats: z.number().int().min(0).max(10_000_000),
    intuneMonthlyActive: z.number().int().min(0).max(10_000_000),
    entraP1UsagePct: z.number().min(0).max(100),
    copilotPaidUnits: z.number().int().min(0).max(10_000_000),
    copilotMonthlyActive: z.number().int().min(0).max(10_000_000),
    e5PaidUnits: z.number().int().min(0).max(10_000_000),
    monthlyProtectedUsersDeployed: z.number().int().min(0).max(10_000_000),
    fy26SecurityWorkshops: z.number().int().min(0).max(1000),
    cspTermsOk: z.boolean(),
    netPaidSeatAdds: z.boolean(),
    timingOk: z.boolean(),
    withinClaimCaps: z.boolean(),
    onPropensityList: z.boolean(),
    eaE5Renewal: z.boolean(),
    mxdrVerified: z.boolean(),
    misaMember: z.boolean(),
})
    .strict();
export const prequalificationDefaults = {
    customerType: 'commercial',
    m365Seats: 0,
    intuneSeats: 0,
    intuneMonthlyActive: 0,
    entraP1UsagePct: 0,
    copilotPaidUnits: 0,
    copilotMonthlyActive: 0,
    e5PaidUnits: 0,
    monthlyProtectedUsersDeployed: 0,
    fy26SecurityWorkshops: 0,
    cspTermsOk: false,
    netPaidSeatAdds: false,
    timingOk: false,
    withinClaimCaps: false,
    onPropensityList: false,
    eaE5Renewal: false,
    mxdrVerified: false,
    misaMember: false,
};
/** The partner's own position: which specializations and designations we hold. */
export const CapabilityRegister = z
    .object({
    specs: z.record(z.string(), SpecStatus),
    designations: z.record(z.string(), DesignationStatus),
    /** What-if toggle: treat `qualified` specializations as held. */
    countQualified: z.boolean(),
    /** Marketplace engagements are blocked until an ISV pairing is confirmed. */
    isvPaired: z.boolean(),
})
    .strict();
export const ScenarioInput = z
    .object({
    /** Schema version, so a stored scenario can be migrated deliberately. */
    schemaVersion: z.literal(1),
    area: AreaKey,
    market: Market,
    segment: Segment,
    /** AUD per 1 USD, scaled by 1e6. Zero means unset — AUD figures show as `—`. */
    fx: FxMicroSchema,
    /** Contract term in months. Affects the deal-value strip only; MCI earns year 1. */
    termMonths: z.union([z.literal(12), z.literal(24), z.literal(36), z.literal(60)]),
    capability: CapabilityRegister,
    acrMode: AcrMode,
    acrPeriod: AcrPeriod,
    acrCurrency: Currency,
    workloads: z.array(WorkloadRow).max(100),
    manualAcr: ManualAcr,
    /** Fold supporting spend into each engagement's band, pro-rata by family share. */
    includeSupporting: z.boolean(),
    seats: z.number().int().min(0).max(10_000_000),
    /** Per seat per month, in `seatPriceCurrency`. Null means unknown, not zero. */
    seatPrice: CentsSchema.nullable(),
    seatPriceCurrency: Currency,
    /** Dynamics 365 incremental annual contract value. Entered in USD. */
    acv: CentsSchema,
    prequalification: Prequalification,
})
    .strict();
export const ScenarioStatus = z.enum(['draft', 'finalized']);
/**
 * The small denormalized last-evaluate result the scenario list's cards show,
 * so listing scenarios never needs to re-run /evaluate for every row.
 */
export const ScenarioSummary = z
    .object({
    incrementalAcrUsd: CentsSchema,
    claimableTotalUsd: CentsSchema,
    eligibleCount: z.number().int().min(0),
    bandLabel: z.string().nullable(),
})
    .strict();
/**
 * A scenario as stored: the input plus who it belongs to and what it is called.
 *
 * `ownerUserId` is the numeric `users.id` primary key — not an Entra `oid`.
 * `oid` is a string that is a real UUID for a genuine Azure sign-in but
 * `local:<id>` under dev-login, and it is not what the `users` table's own
 * primary key is; `request.session.id` (set by apps/api/src/plugins/session.ts
 * from the session cookie, independent of `oid`) is what every scenario route
 * actually keys ownership on. See apps/api/src/routes/scenarios.ts.
 */
export const Scenario = z
    .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    customerName: z.string().max(200).nullable(),
    opportunityRef: z.string().max(120).nullable(),
    guideVersion: z.string().min(1),
    input: ScenarioInput,
    /**
     * CSP promotion quantities and prices. Kept beside `input` rather than
     * inside it: promotions price customer discounts, they are not part of an
     * engagement evaluation, and /evaluate has no business receiving them.
     * Null until the promotions tab has been touched.
     */
    promoInput: PromoScenarioInput.nullable(),
    /**
     * A catalogue from a Partner Center CSV refresh, replacing the snapshot
     * that shipped with the guide. Null means "use the guide's own".
     */
    promoCatalogue: PromoCatalogue.nullable(),
    status: ScenarioStatus,
    ownerUserId: z.number().int().positive(),
    ownerName: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    /** Populated after the scenario has been evaluated at least once. */
    summary: ScenarioSummary.nullable(),
})
    .strict();
/** The body of `POST /scenarios` — everything the server itself assigns is omitted. */
export const CreateScenarioBody = Scenario.omit({
    id: true,
    ownerUserId: true,
    createdAt: true,
    updatedAt: true,
}).strict();
/**
 * The body of `PATCH /scenarios/:id`. Narrower than a client-side
 * `Partial<Scenario>` would be: identity, ownership and timestamps are never
 * client-settable, `updatedAt` is always recomputed server-side.
 */
export const UpdateScenarioBody = Scenario.omit({
    id: true,
    ownerUserId: true,
    createdAt: true,
    updatedAt: true,
})
    .partial()
    .strict();
/**
 * A blank scenario. Every amount opens at zero — the legacy file is explicit that
 * nothing about a deal is assumed, and that is the right default for a tool whose
 * output becomes a financial claim.
 */
export function emptyScenarioInput(defaults) {
    return {
        schemaVersion: 1,
        area: 'azure',
        market: 'A',
        segment: 'SMC-Corporate',
        fx: 0,
        termMonths: 12,
        capability: {
            specs: { ...defaults.specs },
            designations: { ...defaults.designations },
            countQualified: false,
            isvPaired: false,
        },
        acrMode: 'workload',
        acrPeriod: 'month',
        acrCurrency: 'AUD',
        workloads: defaults.workloads.map((w, i) => ({
            id: `w${String(i)}`,
            name: w.name,
            category: w.category,
            amount: 0,
        })),
        manualAcr: {
            incremental: 0,
            baseline: 0,
            ofWhichDatabases: 0,
            ofWhichAgentic: 0,
            ofWhichDefenderSentinel: 0,
        },
        includeSupporting: false,
        seats: 0,
        seatPrice: null,
        seatPriceCurrency: 'AUD',
        acv: 0,
        prequalification: { ...prequalificationDefaults },
    };
}
//# sourceMappingURL=scenario.js.map